import ControlledInput from '@/common/ControllerInput';
import FlatDatePickr from '@/common/Flatpickr';
import ModalComponent from '@/common/Modal';
import { getIsCreateDealerActionCompleted, getIsCreateDealerActionInprogress } from '@/explore/ducks/selectors';
import { callToCreateDealerAction, setIsCreateDealerActionCompleted } from '@/explore/ducks/slice';
import { regexControlNumber, regexEmployeeIdentificationNumber, sotEditValidation } from '@/queue/ducks/validationSchema';
import { getFormattedDate } from '@/queue/elements/value-formatters/date-formatter.mjs';
import TableElement from '@/table/TableElement';
import AppConfig from '@/utils/app-config.mjs';
import { Button, Grid, GridItem, Select, Switch, Table, TableContainer, Tbody, Td, Textarea, Tr, Image } from '@chakra-ui/react';
import React, { useEffect, useRef, useState, MutableRefObject } from 'react'
//@ts-ignore
import InputMask from 'react-input-mask';
import { MapInteractionCSS } from 'react-map-interaction';
import { useDispatch } from 'react-redux';

interface EditSOTProps {
    isOpen: boolean;
    sotDetails: any;
    dealerId: string;
    onClose: () => void;
}

interface OperationTypes {
    [key: string]: {
        name: string,
        rate: number
    }
}

const EditSOT = ({ isOpen, sotDetails, dealerId, onClose }: EditSOTProps) => {

    const startDateSpecialTaxPickerRef = useRef<any>(null);
    const endDateSpecialTaxPickerRef = useRef<any>(null);
    const dateReceiptPickerRef = useRef<any>(null);
    const [licenseReview, setLicenseReview] = useState<any>({})
    const [textareaEvent, setTextareaEvent] = useState<any>(null);
    const dispatch = useDispatch();
    const isCreateDealerActionInprogress = getIsCreateDealerActionInprogress();
    const isCreateDealerActionCompleted = getIsCreateDealerActionCompleted();
    const principalBusinessAddress = useRef(null) as unknown as MutableRefObject<HTMLInputElement>;
    const physicalBusinessAddress = useRef(null) as unknown as MutableRefObject<HTMLInputElement>;

    useEffect(() => {
        principalBusinessAddress?.current?.setSelectionRange(textareaEvent, textareaEvent);
        physicalBusinessAddress?.current?.setSelectionRange(textareaEvent, textareaEvent);
    }, [licenseReview?.principalBusinessAddress, licenseReview?.actualPhysicalBusinessAddress])

    useEffect(() => {
        if (sotDetails) {
            setLicenseReview({
                ...sotDetails,
                principalBusinessAddress: (sotDetails?.principalBusinessName ? (sotDetails?.principalBusinessName + "\n") : "") + (sotDetails?.principalBusinessAddress || ""),
                actualPhysicalBusinessAddress: (sotDetails?.actualPhysicalBusinessName ? (sotDetails?.actualPhysicalBusinessName + "\n") : "") + (sotDetails?.actualPhysicalBusinessAddress || "")
            })
        }
    }, [sotDetails])


    const openStartDateSpecialTaxDatePicker = () => {
        if (startDateSpecialTaxPickerRef.current) {
            startDateSpecialTaxPickerRef.current.setOpen(true);
        }
    };
    const openEndDateSpecialTaxDatePicker = () => {
        if (endDateSpecialTaxPickerRef.current) {
            endDateSpecialTaxPickerRef.current.setOpen(true);
        }
    };

    const openReceiptDatePicker = () => {
        if (dateReceiptPickerRef.current) {
            dateReceiptPickerRef.current.setOpen(true);
        }
    };

    const operationTypes: OperationTypes = {
        "61": {
            name: "Class 1 - Importer of Firearms",
            rate: 1000
        },
        "71": {
            name: "Class 1 - Importer of Firearms (Reduced)",
            rate: 500
        },
        "62": {
            name: "Class 2 - Manufacturer of Firearms",
            rate: 1000
        },
        "72": {
            name: "Class 2 - Manufacturer of Firearms (Reduced)",
            rate: 500
        },
        "63": {
            name: "Class 3 - Dealer in Firearms",
            rate: 500
        }
    };

    const formatEmployeeIdentificationNumber = (value: string) => {
        if (value) {
            value = value.replace(/[a-zA-Z]/g, '');
            if (value.length > 10) return licenseReview?.employeeIdentificationNumber || "";
            else if (value.includes("-")) return value;
            else return value.length > 2 ? `${value?.slice(0, 2)}-${value?.slice(2)}` : value;
        }
        return "";
    };

    const formatControlNumber = (value: string) => {
        if (!value) return ''
        if (value.length !== 13) return value || ""
        else if (value.length === 13) {
            value = value.substring(0, 7) + "-" + value.substring(7, 10) + "-" + value.substring(10)
        }
        return value;
    };


    const validateField = async (name: string, value: string | Date | any) => {
        try {
            if ((["employeeIdentificationNumber"].includes(name) && value?.length === 11 && regexEmployeeIdentificationNumber.test(licenseReview?.employeeIdentificationNumber as string))) {
                setLicenseReview((prevState: any) => ({
                    ...prevState,
                    errors: { ...(prevState.errors || {}), [name]: '' }
                }));
            } else {
                await sotEditValidation.validateAt(name, { [name]: value });
                setLicenseReview((prevState: any) => ({
                    ...prevState,
                    errors: { ...(prevState.errors || {}), [name]: '' }
                }));
            }
        } catch (error: any) {
            if (error.errors && error.errors.length > 0) {
                setLicenseReview((prevState: any) => ({
                    ...prevState,
                    errors: { ...(prevState.errors || {}), [name]: error.errors[0] }
                }));
            }
        }
    };

    const onChangeInputs = async (value: string | boolean | any, fieldName: string) => {
        await validateField(fieldName, value as string);
        if (fieldName === "employeeIdentificationNumber")
            value = formatEmployeeIdentificationNumber(value as string);
        else if (fieldName === "numberOfLocations" && value?.length > 9)
            value = licenseReview?.numberOfLocations;

        if (fieldName === "typeOfOperation") {
            setLicenseReview((prevState: any) => ({
                ...prevState,
                [fieldName]: value,
                annualTaxRate: operationTypes[value as string]?.rate
            }));
            return;
        }
        setLicenseReview((prevState: any) => ({
            ...prevState,
            [fieldName]: value
        }));
    };
    const onDateChange = async (date: Date | null, fieldName: string) => {
        if (!date) {
            setLicenseReview((prevState: any) => ({
                ...prevState,
                [fieldName]: null,
                errors: { ...(prevState.errors || {}), [fieldName]: '' }
            }));
            return;
        }

        setLicenseReview((prevState: any) => ({
            ...prevState,
            [fieldName]: getFormattedDate(date),
            errors: { ...(prevState.errors || {}), [fieldName]: '' }
        }));
    };

    useEffect(() => {
        if (!isCreateDealerActionInprogress && isCreateDealerActionCompleted) {
          onClose();
          dispatch(setIsCreateDealerActionCompleted(false));
        }
      }, [isCreateDealerActionInprogress, isCreateDealerActionCompleted]);

    const onSubmit = async() => {
        const payload: any = {
            sotUpdate: [
                {
                    name: 'edit_sot_history',
                    license_key: licenseReview?.licenseKey,
                    type: 'add',
                    id: licenseReview?.id,
                    value: {
                        dealer_id:dealerId,
                        principal_business_name: licenseReview?.principalBusinessName,
                        name_and_principal_business_address: licenseReview?.principalBusinessAddress,
                        actual_physical_business_name: licenseReview?.actualPhysicalBusinessName,
                        actual_physical_business_address: licenseReview?.actualPhysicalBusinessAddress,
                        date_of_tax_stamp_receipt: licenseReview?.dateOfTaxStampReceipt,
                        employee_identification_number: licenseReview?.employeeIdentificationNumber,
                        type_of_operation: licenseReview?.typeOfOperation,
                        number_of_locations: licenseReview?.numberOfLocations,
                        special_tax_period_start_date: licenseReview?.specialTaxPeriodStartDate,
                        special_tax_period_end_date: licenseReview?.specialTaxPeriodEndDate,
                        control_number: licenseReview?.controlNumber? formatControlNumber(licenseReview?.controlNumber) : "",
                        display_control_number: licenseReview?.controlNumber?.split("-").join("") || null,
                        bio_metric_scanner: licenseReview?.bioMetricScanner,
                    }
                }
            ]
        }
        dispatch(callToCreateDealerAction(payload));
    }

    const fullURL = (path: string) => `${AppConfig.SOTTaxStampsBaseURL}${encodeURIComponent(path)}`;

    return (
        <ModalComponent onClose={() => {
            onClose();
        }}
            isOpen={isOpen}
            title={<>Edit SOT</>}
            variant={2}
            className="sot-tax-stamps-modal"
            footer={
                <>
                    <Button
                        colorScheme="blue"
                        background="#0C1E46"
                        padding="8px 20px"
                        marginRight="20px"
                        isLoading={isCreateDealerActionInprogress}
                        isDisabled={(licenseReview &&
                            licenseReview?.errors &&
                            Object.values(licenseReview?.errors).some((val) => val !== '') ? true : false)}
                        onClick={() => {
                            onSubmit()
                        }}
                    >
                        Submit
                    </Button>
                    <Button
                        colorScheme="blue"
                        background="#ffffff"
                        color="#0C1E46"
                        border="1px solid #0C1E46"
                        padding="8px 11px"
                        onClick={() => {
                            onClose()
                        }}
                    >
                        Cancel
                    </Button>
                </>
            }
        >
            <div>
                <Grid templateColumns={{ base: '1fr', xl: 'repeat(2, 1fr)' }} gap={6}>
                    <GridItem>
                        <TableContainer className='sot-table-container' flex={1}>
                            <Table>
                                <Tbody>
                                    <Grid
                                        templateRows='repeat(1, 1fr)'
                                        templateColumns={{
                                            base: "repeat(1, 1fr)",
                                            md: "repeat(2, 1fr)",
                                        }}
                                        columnGap={6}
                                    >
                                        <Grid
                                            templateRows='50px auto'
                                            templateColumns='repeat(1, 1fr)'
                                        >
                                            <GridItem>
                                                <TableElement noBorder={true} label={'Name and Principal Business Address:'} px={"0"} />
                                            </GridItem>
                                            <GridItem>
                                                <Textarea
                                                    onChange={(event) => {
                                                        onChangeInputs(
                                                            event.target.value,
                                                            'principalBusinessAddress'
                                                        );
                                                        setTextareaEvent(event.target.selectionStart);
                                                        }
                                                    }
                                                    value={licenseReview?.principalBusinessAddress || ""}
                                                    name="principalBusinessAddress"
                                                    className="sot-transfer-form__textarea"
                                                    ref={principalBusinessAddress as any}
                                                />
                                                {licenseReview?.errors && licenseReview?.errors?.principalBusinessAddress ?
                                                    <div className="">
                                                        <div className="edit-error">
                                                            {licenseReview.errors && licenseReview?.errors.principalBusinessAddress && (
                                                                <>* {licenseReview?.errors.principalBusinessAddress}</>
                                                            )}
                                                        </div>
                                                    </div> : ""
                                                }
                                            </GridItem>
                                        </Grid>
                                        <Grid
                                            templateRows='50px'
                                            templateColumns='repeat(1, 1fr)'
                                        >
                                            <GridItem>
                                                <TableElement noBorder={true} label={'Annual Tax Rate:'} px={"0"} />
                                            </GridItem>
                                            <GridItem>
                                                <Tr verticalAlign={"start"}>
                                                    <Td textAlign={'left'} fontWeight={'bold'} p={"0"}>
                                                        {!licenseReview?.typeOfOperation ? "____" : operationTypes[licenseReview?.typeOfOperation as string]?.rate}
                                                    </Td>
                                                </Tr>
                                            </GridItem>
                                        </Grid>
                                        <Grid
                                            templateRows='50px auto'
                                            templateColumns='repeat(1, 1fr)'
                                        >
                                            <GridItem>
                                                <TableElement noBorder={true} label={'Types of Operation Conducted:'} px={"0"} />
                                            </GridItem>
                                            <GridItem sx={{ width: "100%" }}>
                                                <Select
                                                    placeholder='Select option'
                                                    sx={{ borderColor: "black" }}
                                                    onChange={(event) =>
                                                        onChangeInputs(
                                                            event.target.value,
                                                            'typeOfOperation'
                                                        )
                                                    }
                                                >
                                                    {
                                                        Object.entries(operationTypes).map(([key, val]) => <option value={key} selected={key === licenseReview?.typeOfOperation}>({key}) {val.name}</option>)
                                                    }
                                                </Select>
                                                {licenseReview?.errors && licenseReview?.errors?.typeOfOperation ?
                                                    <div className="">
                                                        <div className="edit-error">
                                                            {licenseReview.errors && licenseReview?.errors.typeOfOperation && (
                                                                <>* {licenseReview?.errors.typeOfOperation}</>
                                                            )}
                                                        </div>
                                                    </div> : ""
                                                }
                                            </GridItem>
                                        </Grid>
                                        <Grid
                                            templateRows='50px'
                                            templateColumns='repeat(1, 1fr)'
                                        >
                                            <GridItem>
                                                <TableElement noBorder={true} label={'Number of Locations:'} px={"0"} />
                                            </GridItem>
                                            <GridItem>
                                                <ControlledInput
                                                    className="sot-tax-stamp__input contact-detail-form__contact-name"
                                                    type="text"
                                                    placeholder="Number of Locations"
                                                    value={licenseReview?.numberOfLocations || ""}
                                                    onChange={(event: any) => {
                                                        const value = event.target.value;
                                                        if (/^\d*$/.test(value)) {
                                                            onChangeInputs(value, 'numberOfLocations')
                                                        }
                                                    }}
                                                />
                                                {licenseReview?.errors && licenseReview?.errors?.numberOfLocations ?
                                                    <div className="">
                                                        <div className="edit-error">
                                                            {licenseReview.errors && licenseReview?.errors.numberOfLocations && (
                                                                <>* {licenseReview?.errors.numberOfLocations}</>
                                                            )}
                                                        </div>
                                                    </div> : ""
                                                }
                                            </GridItem>
                                        </Grid>
                                        <Grid
                                            templateRows='50px auto'
                                            templateColumns='repeat(1, 1fr)'
                                        >
                                            <GridItem>
                                                <TableElement noBorder={true} label={'Actual Physical Business Address:'} px={"0"} />
                                            </GridItem>
                                            <GridItem>
                                                <Textarea
                                                    onChange={(event) => {
                                                        onChangeInputs(
                                                            event.target.value,
                                                            'actualPhysicalBusinessAddress'
                                                        );
                                                        setTextareaEvent(event.target.selectionStart);
                                                        }
                                                    }
                                                    value={licenseReview?.actualPhysicalBusinessAddress || ""}
                                                    name="actualPhysicalBusinessAddress"
                                                    className="sot-transfer-form__textarea"
                                                    ref={physicalBusinessAddress as any}
                                                />
                                                {licenseReview?.errors && licenseReview?.errors?.actualPhysicalBusinessAddress ?
                                                    <div className="">
                                                        <div className="edit-error">
                                                            {licenseReview.errors && licenseReview?.errors.actualPhysicalBusinessAddress && (
                                                                <>* {licenseReview?.errors.actualPhysicalBusinessAddress}</>
                                                            )}
                                                        </div>
                                                    </div> : ""
                                                }
                                            </GridItem>
                                        </Grid>
                                        <Grid
                                            templateRows={{
                                                base: '50px auto',
                                            }}
                                            templateColumns='repeat(1, 1fr)'
                                        >
                                            <GridItem>
                                                <TableElement noBorder={true} label={'Dates of Special Tax Period:'} px={"0"} />
                                            </GridItem>
                                            <GridItem>
                                                <Grid
                                                    templateRows={{
                                                        base: 'repeat(2, 1fr)',
                                                        lg: 'repeat(1, 1fr)',
                                                    }}
                                                    templateColumns={{
                                                        base: 'repeat(1, 1fr)',
                                                        lg: 'repeat(2, 1fr)',
                                                    }}
                                                    gap={3}
                                                >
                                                    <GridItem>
                                                        <FlatDatePickr
                                                            label=""
                                                            value={licenseReview?.specialTaxPeriodStartDate || null}
                                                            onDateChange={(date: Date) => onDateChange(date, 'specialTaxPeriodStartDate')}
                                                            options={{ placeholderText: '-Start Date-' }}
                                                            classNameValue="transfer-form__input order-date-picker"
                                                        />
                                                        {licenseReview?.errors && licenseReview?.errors?.specialTaxPeriodStartDate ?
                                                            <div className="">
                                                                <div className="edit-error">
                                                                    {licenseReview.errors && licenseReview?.errors.specialTaxPeriodStartDate && (
                                                                        <>* {licenseReview?.errors.specialTaxPeriodStartDate}</>
                                                                    )}
                                                                </div>
                                                            </div> : ""
                                                        }
                                                    </GridItem>
                                                    <GridItem>
                                                        <FlatDatePickr
                                                            label=""
                                                            value={licenseReview?.specialTaxPeriodEndDate || null}
                                                            onDateChange={(date: Date) => onDateChange(date, 'specialTaxPeriodEndDate')}
                                                            options={{ placeholderText: '-End Date-' }}
                                                            classNameValue="transfer-form__input order-date-picker"
                                                        />
                                                        {licenseReview?.errors && licenseReview?.errors?.specialTaxPeriodEndDate ?
                                                            <div className="">
                                                                <div className="edit-error">
                                                                    {licenseReview.errors && licenseReview?.errors.specialTaxPeriodEndDate && (
                                                                        <>* {licenseReview?.errors.specialTaxPeriodEndDate}</>
                                                                    )}
                                                                </div>
                                                            </div> : ""
                                                        }
                                                    </GridItem>
                                                </Grid>
                                            </GridItem>
                                        </Grid>
                                        <Grid
                                            templateRows='50px'
                                            templateColumns='repeat(1, 1fr)'
                                        >
                                            <GridItem>
                                                <TableElement noBorder={true} label={'Receipt Date:'} px={"0"} />
                                            </GridItem>
                                            <GridItem>
                                                <FlatDatePickr
                                                    label=""
                                                    value={licenseReview?.dateOfTaxStampReceipt || null}
                                                    onDateChange={(date: Date) => onDateChange(date, 'dateOfTaxStampReceipt')}
                                                    options={{ placeholderText: '-End Date-' }}
                                                    classNameValue="transfer-form__input receipt-date-picker"
                                                />
                                                {licenseReview?.errors && licenseReview?.errors?.dateOfTaxStampReceipt ?
                                                    <div className="">
                                                        <div className="edit-error">
                                                            {licenseReview.errors && licenseReview?.errors.dateOfTaxStampReceipt && (
                                                                <>* {licenseReview?.errors.dateOfTaxStampReceipt}</>
                                                            )}
                                                        </div>
                                                    </div> : ""
                                                }
                                            </GridItem>
                                        </Grid>
                                        <Grid
                                            templateRows='50px'
                                            templateColumns='repeat(1, 1fr)'
                                        >
                                            <GridItem>
                                                <TableElement noBorder={true} label={'Control Number:'} px={"0"} />
                                            </GridItem>
                                            <GridItem>
                                                <ControlledInput
                                                    className="sot-customize-input"
                                                    type="text"
                                                    placeholder="Control Number"
                                                    value={licenseReview?.controlNumber || ""}
                                                    onChange={(event: any) =>
                                                        onChangeInputs(
                                                            event.target.value?.trim(),
                                                            'controlNumber'
                                                        )
                                                    }
                                                />
                                                {licenseReview?.errors && licenseReview?.errors?.controlNumber ?
                                                    <div className="">
                                                        <div className="edit-error">
                                                            {licenseReview.errors && licenseReview?.errors.controlNumber && (
                                                                <>* {licenseReview?.errors.controlNumber}</>
                                                            )}
                                                        </div>
                                                    </div> : ""
                                                }
                                            </GridItem>
                                        </Grid>
                                        <Grid
                                            templateRows={{
                                                base: '50px auto',
                                            }}
                                            templateColumns='repeat(1, 1fr)'
                                        >
                                            <GridItem>
                                                <TableElement noBorder={true} label={'Employer Identification Number:'} px={"0"} />
                                            </GridItem>
                                            <GridItem>
                                                <InputMask
                                                    id="formattedInput"
                                                    mask="99-9999999"
                                                    placeholder="XX-XXXXXXX"
                                                    value={licenseReview?.employeeIdentificationNumber || ""}
                                                    onChange={(event: any) =>
                                                        onChangeInputs(
                                                            event.target.value,
                                                            'employeeIdentificationNumber'
                                                        )
                                                    }
                                                >
                                                    {(inputProps: any) => <input
                                                        {...inputProps}
                                                        type="text"
                                                        className='sot-customize-input'
                                                    />
                                                    }
                                                </InputMask>
                                                {licenseReview?.errors && licenseReview?.errors?.employeeIdentificationNumber ?
                                                    <div className="">
                                                        <div className="edit-error">
                                                            {licenseReview.errors && licenseReview?.errors.employeeIdentificationNumber && (
                                                                <>* {licenseReview?.errors.employeeIdentificationNumber}</>
                                                            )}
                                                        </div>
                                                    </div> : ""
                                                }
                                            </GridItem>
                                        </Grid>
                                        <Grid
                                            templateRows='50px'
                                            templateColumns='repeat(1, 1fr)'
                                        >
                                            <GridItem>
                                                <TableElement noBorder={true} label={'Bio Metric Scanner:'} px={"0"} />
                                            </GridItem>
                                            <GridItem>
                                                <Switch
                                                    size='md'
                                                    onChange={(event) =>
                                                        onChangeInputs(
                                                            event.target.checked,
                                                            'bioMetricScanner'
                                                        )
                                                    }
                                                    isChecked={!!licenseReview?.bioMetricScanner}
                                                />
                                                {licenseReview?.errors && licenseReview?.errors?.bioMetricScanner ?
                                                    <div className="">
                                                        <div className="edit-error">
                                                            {licenseReview.errors && licenseReview?.errors.bioMetricScanner && (
                                                                <>* {licenseReview?.errors.bioMetricScanner}</>
                                                            )}
                                                        </div>
                                                    </div> : ""
                                                }
                                            </GridItem>
                                        </Grid>
                                    </Grid>
                                </Tbody>
                            </Table>
                        </TableContainer>
                    </GridItem>
                    <GridItem>
                        <TableContainer flex={1}>
                            <Table>
                                <Tbody>
                                    <Grid
                                        templateRows='repeat(1, 1fr)'
                                        templateColumns='repeat(1, 1fr)'
                                        pb={10}
                                    >
                                        <Grid
                                            templateRows='50px 450px'
                                            templateColumns='repeat(1, 1fr)'
                                        >
                                            <GridItem>
                                                <TableElement noBorder={true} label={'SOT Preview:'} px={"0"} />
                                            </GridItem>
                                            <GridItem
                                                w={"100%"}
                                                h={"100%"}
                                            >
                                                {
                                                    licenseReview?.licensePath && !(licenseReview?.licensePath.split('.').pop() === 'pdf') ?
                                                    <MapInteractionCSS
                                                        showControls
                                                        defaultValue={{
                                                            scale: 1,
                                                            translation: { x: 0, y: 20 }
                                                        }}
                                                        minScale={0.5}
                                                        maxScale={3}
                                                        translationBounds={{
                                                            xMax: 400,
                                                            yMax: 200
                                                        }}
                                                        btnClass={'map-interection-buttons'}
                                                    >
                                                        <Image src={licenseReview?.licensePath ? fullURL(licenseReview?.licensePath) : ""} alt='sot-image' className={`sot-image`} />
                                                    </MapInteractionCSS>
                                                    : <iframe src={licenseReview?.licensePath ? fullURL(licenseReview?.licensePath) : ""} name={"SOT License Preview"} title={"SOT License Preview"} id='sot-license-iframe' className='sot-license-preview'></iframe>
                                                }
                                            </GridItem>
                                        </Grid>
                                    </Grid>
                                </Tbody>
                            </Table>
                        </TableContainer>
                    </GridItem>
                </Grid>
            </div>
        </ModalComponent>
    )
}

export default EditSOT
